import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { fmtTime, addMinutes, isoNoMs } from "@/lib/format";
import { parseTime, setIsoTime } from "@/lib/timeParse";
import PopoverPortal from "./PopoverPortal";

/**
 * Click-to-edit time cell.
 *
 * - Single click → switches to a text input pre-filled with current HH:MM.
 *   Type "0800", "8a", "1430", "2:30p" — Enter commits, Esc cancels.
 * - Below the input, a stepper popover (-1h / -15 / +15 / +1h) is always
 *   available as a quick adjust without typing.
 * - When the commit changes the start time, the block slides — duration is
 *   preserved (the parent computes end_at = start + duration in onCommit).
 */
export default function TimeEditor({ value, onCommit, disabled, dataTestid }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  const btnRef = useRef(null);
  const inputRef = useRef(null);
  const display = fmtTime(value);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const adjust = (mins) => {
    const next = isoNoMs(addMinutes(value, mins));
    onCommit(next);
  };

  const commitText = () => {
    const parsed = parseTime(text);
    if (parsed) {
      const next = setIsoTime(value, parsed);
      if (next !== value) onCommit(next);
    }
    setEditing(false);
  };

  const cancel = () => {
    setText(display);
    setEditing(false);
  };

  return (
    <div onClick={(e) => e.stopPropagation()}>
      {editing && !disabled ? (
        <input
          ref={inputRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={commitText}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitText();
            } else if (e.key === "Escape") {
              e.preventDefault();
              cancel();
            }
          }}
          className="mono text-xs tabular-nums px-1 w-14 bg-bg-elevated text-amber border border-amber/60 outline-none"
          data-testid={dataTestid ? `${dataTestid}-input` : undefined}
          placeholder="HH:MM"
          spellCheck={false}
          autoComplete="off"
        />
      ) : (
        <button
          ref={btnRef}
          className={clsx(
            "mono text-xs tabular-nums px-1",
            disabled ? "text-ink-mute cursor-not-allowed" : "text-ink hover:text-amber"
          )}
          onClick={() => {
            if (disabled) return;
            setText(display);
            setEditing(true);
          }}
          disabled={disabled}
          data-testid={dataTestid}
          title="Click to type a new time — 0800, 8a, 14:30 all work"
        >
          {display}
        </button>
      )}
      <PopoverPortal anchorRef={editing ? inputRef : btnRef} open={editing && !disabled} onClose={() => {}}>
        <div className="bg-bg-elevated border border-line-strong shadow-[0_8px_24px_rgba(0,0,0,0.7)] p-1 flex gap-px mt-1">
          <button
            // Use onMouseDown so the input's onBlur doesn't fire first and
            // close the editor before our handler runs.
            onMouseDown={(e) => { e.preventDefault(); adjust(-60); }}
            className="btn-ghost px-1.5 py-0.5"
          >-1h</button>
          <button onMouseDown={(e) => { e.preventDefault(); adjust(-15); }} className="btn-ghost px-1.5 py-0.5">-15</button>
          <button onMouseDown={(e) => { e.preventDefault(); adjust(15); }} className="btn-ghost px-1.5 py-0.5">+15</button>
          <button onMouseDown={(e) => { e.preventDefault(); adjust(60); }} className="btn-ghost px-1.5 py-0.5">+1h</button>
        </div>
      </PopoverPortal>
    </div>
  );
}
